import { useCallback, useEffect, useRef } from 'react';

export type UseAutoResizeTextareaOptions = {
  minHeight?: number;
  maxHeight?: number;
};

export function useAutoResizeTextarea(
  options: UseAutoResizeTextareaOptions = {},
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { minHeight = 120, maxHeight } = options;

  const resize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const hadFocus = document.activeElement === textarea;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;

    // Store current height for transition
    const currentHeight = textarea.offsetHeight;

    // Calculate what the height should be by temporarily measuring
    textarea.style.height = 'auto';
    const newHeight = Math.max(minHeight, textarea.scrollHeight);
    const finalHeight = maxHeight ? Math.min(newHeight, maxHeight) : newHeight;

    // Restore current height first, then transition to new height
    textarea.style.height = `${currentHeight}px`;

    // Force reflow, then set new height for transition
    textarea.offsetHeight; // Force reflow
    textarea.style.height = `${finalHeight}px`;

    textarea.style.overflowY =
      maxHeight && newHeight > maxHeight ? 'auto' : 'hidden';

    // Changing height (especially via `height: auto`) can move the caret to the
    // end in some browsers; restore after layout so it runs after React applies
    // controlled `value` for the same tick.
    if (hadFocus) {
      const len = textarea.value.length;
      const start = Math.min(selectionStart, len);
      const end = Math.min(selectionEnd, len);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el || document.activeElement !== el) return;
        el.setSelectionRange(start, end);
        requestAnimationFrame(() => {
          const el2 = textareaRef.current;
          if (!el2 || document.activeElement !== el2) return;
          el2.setSelectionRange(start, end);
        });
      });
    }
  }, [minHeight, maxHeight]);

  const handleInput = useCallback(
    (event: React.FormEvent<HTMLTextAreaElement>) => {
      resize();

      // Call original onInput if provided
      const target = event.target as HTMLTextAreaElement;
      const originalOnInput = target.oninput;
      if (originalOnInput) {
        originalOnInput.call(target, event.nativeEvent);
      }
    },
    [resize],
  );

  // Resize on mount and when content changes
  useEffect(() => {
    resize();
  }, [resize]);

  return {
    textareaRef,
    handleInput,
    resize,
  };
}
